import { useCallback, useEffect, useRef } from 'react';
import { useStore } from './useStore';
import { NotificationType } from '../types';

interface NotificationOptions {
  title: string;
  message: string;
  type?: NotificationType;
  duration?: number;
}

export function useNotifications() {
  const { notifications, addNotification, removeNotification } = useStore();
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const notify = useCallback((options: NotificationOptions) => {
    const notification = {
      title: options.title,
      message: options.message,
      type: options.type || 'info' as NotificationType,
      duration: options.duration ?? 5000
    };

    addNotification(notification);
  }, [addNotification]);

  const success = useCallback((title: string, message: string) => {
    notify({ title, message, type: 'success' });
  }, [notify]);

  const error = useCallback((title: string, message: string) => {
    notify({ title, message, type: 'error', duration: 8000 });
  }, [notify]);

  const warning = useCallback((title: string, message: string) => {
    notify({ title, message, type: 'warning' });
  }, [notify]);

  const info = useCallback((title: string, message: string) => {
    notify({ title, message, type: 'info' });
  }, [notify]);

  const dismiss = useCallback((id: string) => {
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
    removeNotification(id);
  }, [removeNotification]);

  const dismissAll = useCallback(() => {
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current.clear();
    notifications.forEach((n) => removeNotification(n.id));
  }, [notifications, removeNotification]);

  // Auto-dismiss notifications
  useEffect(() => {
    notifications.forEach((notification) => {
      if (!timeoutsRef.current.has(notification.id) && notification.duration) {
        const timeout = setTimeout(() => {
          removeNotification(notification.id);
          timeoutsRef.current.delete(notification.id);
        }, notification.duration);
        
        timeoutsRef.current.set(notification.id, timeout);
      }
    });

    return () => {
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    };
  }, [notifications, removeNotification]);

  return {
    notifications,
    notify,
    success,
    error,
    warning,
    info,
    dismiss,
    dismissAll
  };
}

export default useNotifications;
